import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Button, Flex, Text, Box } from '@radix-ui/themes';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';

interface SessionTimeoutWarningProps {
  isOpen: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
}

// Auto-lock countdown: if the user does not click "Stay Logged In" within this
// many seconds of the warning appearing, the session is locked automatically.
const AUTO_LOCK_SECONDS = 10;

/**
 * Modal warning component that displays when session is about to expire.
 * Shows countdown and provides options to extend session or logout immediately.
 *
 * PHA-676: Added 10-second auto-lock. When the modal opens, a countdown begins.
 * If the user takes no action within AUTO_LOCK_SECONDS, onLogout is called
 * automatically (HIPAA screen-lock requirement). "Stay Logged In" cancels the
 * auto-lock. The countdown is displayed so the user can see how long they have.
 */
export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  isOpen,
  onExtend,
  onLogout,
}) => {
  const [autoLockCountdown, setAutoLockCountdown] = useState<number>(AUTO_LOCK_SECONDS);
  const autoLockTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start or cancel the auto-lock timer whenever the modal opens/closes.
  useEffect(() => {
    if (isOpen) {
      // Reset countdown display.
      setAutoLockCountdown(AUTO_LOCK_SECONDS);

      // Decrement the visible countdown every second.
      countdownIntervalRef.current = setInterval(() => {
        setAutoLockCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);

      // After AUTO_LOCK_SECONDS, force logout regardless of user action.
      autoLockTimerRef.current = setTimeout(() => {
        onLogout();
      }, AUTO_LOCK_SECONDS * 1000);
    } else {
      // Modal closed (either by extend or logout) — cancel pending auto-lock.
      if (autoLockTimerRef.current) {
        clearTimeout(autoLockTimerRef.current);
        autoLockTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (autoLockTimerRef.current) {
        clearTimeout(autoLockTimerRef.current);
        autoLockTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
    // onLogout is memoized-stable in AuthContext — safe in dep array.
  }, [isOpen, onLogout]);

  const handleExtend = () => {
    // Cancel the auto-lock before calling the parent extend handler.
    if (autoLockTimerRef.current) {
      clearTimeout(autoLockTimerRef.current);
      autoLockTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    onExtend();
  };

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Content style={{ maxWidth: 450 }}>
        <Dialog.Title>
          <Flex gap="2" align="center">
            <ExclamationTriangleIcon
              width="24"
              height="24"
              color="var(--red-9)"
            />
            <Text>Session Expiring Soon</Text>
          </Flex>
        </Dialog.Title>

        <Dialog.Description size="2" mb="4">
          {/* PHA-713: Show one clear deadline — the 10s auto-lock — instead of two
              conflicting timers (server session remaining vs auto-lock countdown). */}
          <Box mt="3">
            <Text weight="bold" style={{ color: 'var(--red-9)', fontSize: '1.1em' }}>
              You will be logged out in {autoLockCountdown}s due to inactivity.
            </Text>
          </Box>
          <Box mt="3">
            <Text size="2" color="gray">
              Click "Stay Logged In" to continue your session, or "Logout Now" to end it immediately.
            </Text>
          </Box>
        </Dialog.Description>

        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={onLogout}
          >
            Logout Now
          </Button>
          <Button
            onClick={handleExtend}
          >
            Stay Logged In
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default SessionTimeoutWarning;
